import React, { useState, useEffect } from "react";
import { canUseFeature, incrementUsage } from "../utils/usageLimit";
import { FREE_LIMITS } from "../utils/limits";
import ProUpgradeDialog from "./ProUpgradeDialog";
import { getAuthHeader } from "../utils/supabaseClient";
import { reportTiming } from "../utils/reportTiming";
import {
  Dialog,
  DialogContent,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Button,
  TextField,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";

const TripPlannerModal = ({
  open,
  onClose,
  destination,
  startDate,
  endDate,
  isPro,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [itinerary, setItinerary] = useState("");
  const [error, setError] = useState("");
  const [notes, setNotes] = useState("");
  const [limitReached, setLimitReached] = useState(false);

  const formatDate = (d) => {
    if (!d) return "";
    return new Date(d).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  useEffect(() => {
    if (open && destination && !itinerary) {
      handleGenerate();
    }
  }, [open, destination]);

  const handleGenerate = async () => {
    if (!canUseFeature("tripplanner", FREE_LIMITS.tripplanner, isPro)) {
      setLimitReached(true);
      return;
    }
    incrementUsage("tripplanner");
    setLoading(true);
    setIsStreaming(false);
    setError("");
    setItinerary("");

    const planT0 = Date.now();
    let planSuccess = false;
    let planErr = null;
    try {
      const response = await fetch(
        "https://flybnb.up.railway.app/api/plan-trip-stream",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(await getAuthHeader()) },
          body: JSON.stringify({
            destination,
            startDate: formatDate(startDate),
            endDate: formatDate(endDate),
            notes,
          }),
        }
      );

      if (!response.ok) throw new Error(`Server error ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let firstChunk = true;
      setIsStreaming(true);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6).trim();
          if (data === "[DONE]") break;
          try {
            const json = JSON.parse(data);
            if (json.error) throw new Error(json.error);
            if (json.text) {
              if (firstChunk) { setLoading(false); firstChunk = false; }
              setItinerary((prev) => prev + json.text);
            }
          } catch {}
        }
      }
      planSuccess = true;
    } catch (err) {
      console.error(err);
      setError("Could not generate plan. Please try again.");
      planErr = err?.message;
    } finally {
      setLoading(false);
      setIsStreaming(false);
      reportTiming(
        "tripplan",
        Date.now() - planT0,
        planSuccess,
        { dest: destination },
        planErr
      );
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(itinerary);
  };

  // --- 🛠️ SMART PARSER: Converts Markdown -> UI ---
  const renderFormattedItinerary = (text) => {
    if (!text) return null;

    // Helper to bold text inside **...**
    const parseBold = (str) => {
      const parts = str.split(/\*\*(.*?)\*\*/g);
      return parts.map((part, i) =>
        i % 2 === 1 ? (
          <Box
            component="span"
            key={i}
            sx={{ fontWeight: 800, color: theme.palette.text.primary }}
          >
            {part}
          </Box>
        ) : (
          <span key={i}>{part}</span>
        )
      );
    };

    return text.split("\n").map((line, index) => {
      const trimmed = line.trim();
      if (!trimmed) return <Box key={index} sx={{ mb: 1 }} />; // Spacer

      // ✅ 1. HEADERS (### The Vibe) - THIS WAS MISSING
      if (trimmed.startsWith("###")) {
        const title = trimmed.replace(/^###\s*/, "");
        return (
          <Typography
            key={index}
            variant="h6"
            sx={{
              mt: 2.5,
              mb: 1,
              fontWeight: "bold",
              color: theme.palette.primary.main, // Brand color title
              textTransform: "uppercase",
              fontSize: "0.95rem",
              letterSpacing: 0.5,
            }}
          >
            {title}
          </Typography>
        );
      }

      // ✅ 2. BULLET POINTS (* Item)
      if (trimmed.startsWith("* ") || trimmed.startsWith("- ")) {
        // Remove the bullet char
        const content = trimmed.substring(2);
        return (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              mb: 1,
              ml: 1,
            }}
          >
            <Box
              sx={{
                minWidth: "6px",
                height: "6px",
                borderRadius: "50%",
                mt: 1, // Align dot with text top
                bgcolor: theme.palette.secondary.main,
              }}
            />
            <Typography
              variant="body2"
              sx={{ lineHeight: 1.6, color: "text.secondary" }}
            >
              {parseBold(content)}
            </Typography>
          </Box>
        );
      }

      // ✅ 3. STANDARD PARAGRAPHS
      return (
        <Typography
          key={index}
          variant="body1"
          sx={{ mb: 1.5, lineHeight: 1.6 }}
        >
          {parseBold(trimmed)}
        </Typography>
      );
    });
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          backgroundImage: isMobile ? "none" :
            theme.palette.mode === "dark"
              ? "linear-gradient(145deg, #1e1e1e, #121212)"
              : "linear-gradient(145deg, #ffffff, #f5f5f7)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AutoAwesomeIcon sx={{ color: theme.palette.secondary.main }} />
          <Typography variant="h6" fontWeight="bold">
            Trip Helper AI
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ pb: 3 }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Planning your trip to <strong>{destination}</strong>
          {startDate && ` from ${formatDate(startDate)}`}
          {endDate && ` to ${formatDate(endDate)}`}.
        </Typography>

        {/* Input Section */}
        <Box
          sx={{
            mb: 3,
            p: 2,
            borderRadius: 2,
            bgcolor:
              theme.palette.mode === "dark"
                ? "rgba(255,255,255,0.03)"
                : "rgba(0,0,0,0.02)",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="caption"
            fontWeight="bold"
            sx={{ mb: 1, display: "block", color: "text.secondary" }}
          >
            CUSTOMIZE YOUR VIBE
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="e.g. I love history, spicy food, and avoiding crowds..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !loading) {
                  e.preventDefault(); // Prevent accidental form refreshes
                  handleGenerate();
                }
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: theme.palette.background.paper,
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={loading}
              sx={{
                minWidth: "50px",
                px: 2,
                bgcolor: theme.palette.secondary.main,
                "&:hover": { bgcolor: theme.palette.secondary.dark },
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <RefreshIcon />
              )}
            </Button>
          </Box>
        </Box>

        {/* Output Section */}
        {loading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 6 }}>
            <CircularProgress size={30} sx={{ color: theme.palette.secondary.main, mb: 2 }} />
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Crafting your perfect itinerary...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <Typography color="error" variant="body2">{error}</Typography>
            <Button onClick={handleGenerate} sx={{ mt: 1 }}>Try Again</Button>
          </Box>
        ) : itinerary ? (
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.6)",
              border: `1px solid ${theme.palette.divider}`,
              position: "relative",
              boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
            }}
          >
            <Box>
              {renderFormattedItinerary(itinerary)}
              {isStreaming && (
                <Box
                  component="span"
                  sx={{
                    display: "inline-block",
                    width: "2px",
                    height: "1em",
                    bgcolor: "text.primary",
                    ml: "2px",
                    verticalAlign: "text-bottom",
                    "@keyframes blink": { "50%": { opacity: 0 } },
                    animation: "blink 0.8s step-end infinite",
                  }}
                />
              )}
            </Box>

            {!isStreaming && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    startIcon={<ContentCopyIcon />}
                    size="small"
                    onClick={handleCopy}
                    sx={{ textTransform: "none", color: "text.secondary", fontSize: "0.8rem" }}
                  >
                    Copy to clipboard
                  </Button>
                </Box>
              </>
            )}
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
    <ProUpgradeDialog
      open={limitReached}
      onClose={() => setLimitReached(false)}
      feature="Trip Planner AI"
      limit={FREE_LIMITS.tripplanner}
    />
    </>
  );
};

export default TripPlannerModal;
